import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

/**
 * Action Button Component
 *
 * Standardized button variants for report page actions.
 * Uses class-variance-authority for type-safe variants.
 *
 * Variants (toate pe tokeni). Cifrele de mai jos sunt CALCULATE din valorile
 * tokenilor din main.css (sRGB, formula WCAG), NU masurate pe pixel randat.
 * Ele presupun ca fundalul efectiv e cel din varianta; un `className` de la
 * consumator sau un fundal de rand diferit le schimba. De masurat: S1 (A) si
 * S3 (B) — vezi descrierea PR-ului.
 * - edit:      conturat, bordura `--color-info` (3,68 pe `bg-card`), text
 *              `--color-dark` (8,19). La HOVER fundalul devine `--color-muted`
 *              si bordura albastra ar cadea la 2,87, sub pragul de 3:1 pentru
 *              non-text — de aceea `hover:border-dark` (6,39). Blana albastra
 *              ramane in repaus, unde poarta codul de culoare.
 * - approve:   umplut `--color-success`, alb (5,48)
 * - reject:    umplut `--color-error`, alb (6,47)
 *
 *   ⚠️ approve/reject/delete NU folosesc `hover:bg-<token>/90`. O tinta cu
 *   alfa se compune cu fundalul RANDULUI, care e deschis, deci hover-ul
 *   DESCHIDE culoarea si contrastul textului alb SCADE: `delete` cadea la
 *   4,22-4,46 (sub 4,5) pe toate fundalurile de rand, iar `approve` la 4,53,
 *   adica trecea cu 0,03. Hover-ul inchide acum culoarea, prin tokenii
 *   `--color-*-hover` din main.css: 7,68 / 8,31 / 7,09. Calculat, ca restul.
 * - process:   umplut `--color-dark`, alb (8,19)
 * - delete:    umplut `--color-warning`, alb (5,02)
 * - primary:   umplut `--color-magenta`, alb (7,40)
 * - ghost:     text `--color-dark`, subliniat (FARA fundal propriu: mosteneste
 *              `--ecotic-row-bg`. Calculat pe cele trei fundaluri de rand —
 *              alb 8,19 / gray-50 la hover 7,84 / `--color-light` pe randul de
 *              podium 6,39. `SchoolRequestsReportPage.tsx:348` ii trece
 *              `className="text-error"`, care BATE `text-dark` prin `cn()`:
 *              6,47 / 6,19 / 5,05. Toate peste 4,5, dar e o suprascriere de
 *              consumator, deci se remasoara daca se schimba fundalul.)
 *
 * Sizes (inaltimea minima e 44px sub `md`, prin clasa de baza):
 * - sm: px-3 py-1 text-small (md:px-2) - implicit pentru actiunile din tabel
 * - md: px-3 py-2 text-small
 * - lg: px-4 py-2 text-base
 *
 * @example
 * ```tsx
 * <ActionButton intent="approve" onClick={handleApprove}>
 *   Aprobă
 * </ActionButton>
 *
 * <ActionButton intent="edit" size="md" disabled={isLoading}>
 *   Editează
 * </ActionButton>
 * ```
 */

const actionButtonVariants = cva(
  // Base styles (always applied)
  // `min-h-11 md:min-h-0`: 44px sub `md`, densitatea de tabel inapoi pe desktop.
  'inline-flex min-h-11 items-center justify-center rounded-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none md:min-h-0',
  {
    variants: {
      intent: {
        // ⚠️ `edit` e SINGURUL conturat, si nu din gust: paleta n-are niciun
        // albastru care sa suporte text alb. `--color-info` cu alb da 3,68 —
        // sub prag. Bordura pastreaza codul de culoare (3,68 trece pragul de
        // 3:1 pentru non-text), iar textul urca la 8,19 pe `--color-dark`.
        // Conturat citeste si ca emfaza mai mica decat aproba/respinge/sterge,
        // ceea ce e corect pentru „editeaza".
        edit: 'border border-info bg-card text-dark hover:bg-muted hover:border-dark focus:ring-info',
        approve: 'bg-success text-white hover:bg-success-hover focus:ring-success',
        reject: 'bg-error text-white hover:bg-error-hover focus:ring-error',
        // Violetul n-are echivalent in paleta. `--color-dark` e neutru, distinct
        // de celelalte patru, si potrivit pentru o actiune de logistica.
        process: 'bg-dark text-white hover:bg-dark-hover focus:ring-dark',
        delete: 'bg-warning text-white hover:bg-warning-hover focus:ring-warning',
        primary: 'bg-magenta text-white hover:bg-magenta-hover focus:ring-magenta',
        ghost: 'text-dark underline hover:no-underline focus:ring-dark',
      },
      size: {
        // `text-xs` (12px) era pe TOATE butoanele de actiune din rapoarte,
        // pentru ca `sm` e varianta implicita. Trece pe `text-small` (15,2px).
        // ⚠️ NU e "acelasi prag ca Button" — asa scria aici, si e fals:
        // `Button` e pe `text-sm`, adica 14px. ActionButton e cu 1,2px mai mare.
        sm: 'px-3 py-1 text-small md:px-2',
        md: 'px-3 py-2 text-small',
        lg: 'px-4 py-2 text-base',
      },
    },
    defaultVariants: {
      intent: 'primary',
      size: 'sm',
    },
  }
);

export interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof actionButtonVariants> {
  /**
   * Button content (text or JSX)
   */
  children: React.ReactNode;
  /**
   * Loading state - shows loading spinner and disables button
   */
  isLoading?: boolean;
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ className, intent, size, children, disabled, isLoading, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(actionButtonVariants({ intent, size }), className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

ActionButton.displayName = 'ActionButton';

export default ActionButton;